/**
 * SlugPreview.tsx
 * Feature: 155 - Telemetry Pipeline Provisioning & Management
 * Author: Peter Dobler (Dobler Data Solutions)
 * Date: 2026-05-19
 * Constitution Version: v1.12.0 | Principle XXXVII (Code Header Standards)
 *
 * Renders a real-time slug + eventHubName preview with availability indicator.
 * Debounces 300 ms after displayName changes before calling POST /slug-preview.
 */

import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui';
import type { SlugPreviewResponse } from '@shared/types/telemetryPipeline';

interface SlugPreviewProps {
  displayName: string;
}

const SlugPreview: React.FC<SlugPreviewProps> = ({ displayName }) => {
  const [preview, setPreview] = useState<SlugPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!displayName.trim()) {
      setPreview(null);
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/telemetry-pipelines/slug-preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ displayName }),
        });
        if (res.ok) {
          const data = (await res.json()) as SlugPreviewResponse;
          setPreview(data);
        }
      } catch { /* ignore */ } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [displayName]);

  if (!displayName.trim()) return null;

  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <div className="h-7 w-[120px] animate-pulse rounded-[var(--radius-md)] bg-[var(--color-bg-subtle)]" />
        <div className="h-7 w-[200px] animate-pulse rounded-[var(--radius-md)] bg-[var(--color-bg-subtle)]" />
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Badge variant="default" className="font-mono text-xs">
        {preview.slug}
      </Badge>
      <Badge variant="default" className="font-mono text-xs">
        {preview.eventHubName}
      </Badge>
      {preview.isAvailable ? (
        <div className="flex items-center gap-1 text-[var(--color-status-success)]">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-xs">Available</span>
        </div>
      ) : (
        <div className="flex items-center gap-1 text-[var(--color-status-danger)]">
          <XCircle className="h-4 w-4" />
          <span className="text-xs">{preview.conflictMessage ?? 'Unavailable'}</span>
        </div>
      )}
    </div>
  );
};

export default SlugPreview;
